import { Button, Input, Select, Space, TableProps } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import DoubleTick from "../../../../assets/icons/DoubleTick";
import EditIcon from "../../../../assets/icons/EditIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useAddEmployeeAdditionalFieldMutation,
  useGetAdditionalDataQuery,
  useGetEmployeeAdditionalFieldsQuery,
  useRemoveAdditionalDataMutation,
  useUpdateEmployeeAdditionalFieldMutation,
} from "../../../../features/employeeAdditionalField/employeeAdditionalFieldApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import {
  addKeyInArray,
  convertToSnakeCase,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";

type RowType = {
  id?: number | string;
  name?: string;
  type?: string;
  section_id?: number;
  isChange?: boolean;
};

export default function EmployeeCustomFields() {
  const [rows, setRows] = useState<RowType[]>([]);
  const { open, handleOpen, handleClose } = useModal<
    number | "new",
    "REMOVE"
  >();
  const { authInfo } = useAppSelector((state) => state.auth);

  const {
    isLoading: loading,
    isError: isGetError,
    error: getError,
    data: getData,
  } = useGetEmployeeAdditionalFieldsQuery({ params: { skip: 0, limit: 0 } });
  const [
    addEmployeeAdditionalField,
    {
      isLoading: isAddLoading,
      isError: isAddError,
      error: addError,
      isSuccess: isAddSuccess,
    },
  ] = useAddEmployeeAdditionalFieldMutation();
  const [
    updateEmployeeAdditionalField,
    { isLoading, isError, error, isSuccess },
  ] = useUpdateEmployeeAdditionalFieldMutation();
  const [
    removeAdditionalData,
    {
      isLoading: isRemoveLoading,
      isError: isRemoveError,
      error: removeError,
      isSuccess: isRemoveSuccess,
    },
  ] = useRemoveAdditionalDataMutation();
  const {
    isLoading: additionalDataLoading,
    data: additionalData,
    isError: isAdditionalError,
    error: additionalError,
  } = useGetAdditionalDataQuery({});
  useError(isGetError, getError, "Get additional field data fail");
  useError(isAddError, addError, "Add additional field data fail");
  useError(isError, error, "Update additional field data fail");
  useError(isAdditionalError, additionalError, "Get additional data fail");
  useError(isRemoveError, removeError, "Delete additional data fail");

  const handleNewRow = () => {
    const newRowData = { id: "new", name: "", type: "string", isChange: true };

    setRows((prev) => [
      ...prev?.map((item) => ({ ...item, isChange: false })),
      newRowData,
    ]);
  };

  const handleSubmit = (id: number | string) => {
    const data = findData(id);
    const newData = {
      section_id: data?.section_id,
      name: convertToSnakeCase(data?.name || ""),
      label: data?.name,
      type: data?.type,
      rules:
        data?.type === "file"
          ? {
              extensions: ["png", "jpeg", "jpg", "docx", "pdf"],
            }
          : null,
    };

    if (data?.id === "new") {
      addEmployeeAdditionalField(newData);
    } else if (data?.id) {
      updateEmployeeAdditionalField({
        id: data?.id?.toString(),
        body: newData,
      });
    }
  };

  const findData = (id: number | string) => {
    return rows.find((item) => item.id === id);
  };

  const handleChange = (
    id: number | string,
    name: "name" | "type" | "isChange" | "section_id",
    value: string | boolean | number
  ) => {
    const newData = rows?.map((item) => {
      if (item?.id === id) {
        return {
          ...item,
          [name]: value,
        };
      }

      return {
        ...item,
        isChange: false,
      };
    });
    setRows(newData);
  };

  const handleRemove = () => {
    if (open?.data === "new") {
      setRows((prev) => prev?.filter((item) => item?.id !== "new"));
      handleClose();
      toast("success", "Additional field delete successfully");
      return;
    }

    if (open?.data && authInfo?.id) {
      removeAdditionalData({
        id: open?.data,
        businessMemberId: authInfo?.id,
      });
    }
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Additional Section",
      width: 250,
      render: (val) => (
        <Select
          allowClear
          showSearch
          value={findData(val?.id)?.section_id}
          onChange={(e) => handleChange(val?.id, "section_id", e)}
          disabled={val?.id !== "new" && !val?.isChange}
          placeholder="Select"
          filterOption={selectSearchOption}
          options={
            additionalData?.data?.additionalSections?.length
              ? selectArrayOption(
                  additionalData?.data?.additionalSections,
                  "name",
                  "id"
                )
              : []
          }
          className="w-full"
          suffixIcon={<SelectDownIcon />}
          rootClassName="icon-gray"
        />
      ),
    },
    {
      title: "Label",
      render: (val) => (
        <div>
          <Input
            placeholder="This is a Input field"
            value={findData(val?.id)?.name}
            onChange={(e) => handleChange(val?.id, "name", e.target.value)}
            disabled={val?.id !== "new" && !val?.isChange}
          />
        </div>
      ),
    },

    {
      title: "Type",
      width: 250,
      render: (val) => (
        <Select
          allowClear
          showSearch
          value={findData(val?.id)?.type}
          onChange={(e) => handleChange(val?.id, "type", e)}
          disabled={val?.id !== "new" && !val?.isChange}
          placeholder="Select"
          filterOption={selectSearchOption}
          options={typeList}
          className="w-full"
          suffixIcon={<SelectDownIcon />}
          rootClassName="icon-gray"
        />
      ),
    },
    {
      title: "Action",
      width: 150,
      render: (val, __, index) => (
        <Space size={10}>
          {val?.isChange || val?.id === "new" ? (
            <Button
              icon={<DoubleTick />}
              className="middle-button !w-8 !h-8 rounded-full !border-[#8FE7B8] !text-[#1DB469]"
              loading={isAddLoading || isLoading}
              onClick={() => handleSubmit(val?.id)}
            />
          ) : (
            <Button
              icon={<EditIcon />}
              className="middle-button !w-8 !h-8 rounded-full !border-[#B4C4FF] !text-[#4A72FF]"
              onClick={() => {
                handleChange(val?.id, "isChange", true);
                setRows((prev) => prev?.filter((item) => item?.id !== "new"));
              }}
            />
          )}
          <Button
            icon={<DeleteIcon />}
            onClick={() => handleOpen({ type: "REMOVE", data: val?.id })}
            className="middle-button !w-8 !h-8 rounded-full !border-[#FFC5C1] !text-[#FF574D]"
          />
          {index === rows?.length - 1 &&
            !rows?.find((item) => item?.id === "new") && (
              <Button
                type="primary"
                icon={<AddIcon />}
                className="middle-button !w-8 !h-8 rounded-full"
                onClick={handleNewRow}
              />
            )}
        </Space>
      ),
    },
  ];

  const emptyColumns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
    },
    {
      title: "Additional Section",
      width: 250,
    },
    {
      title: "Label",
    },

    {
      title: "Type",
      width: 250,
    },
    {
      title: "Action",
      width: 150,
      render: () => (
        <Space size={10}>
          <Button
            type="primary"
            icon={<AddIcon />}
            className="middle-button !w-8 !h-8 rounded-full"
            onClick={handleNewRow}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (getData?.data?.additionalFields?.length) {
      setRows(
        getData?.data?.additionalFields?.map((item) => ({
          id: item?.id,
          name: item?.label,
          type: item?.type,
          section_id: item?.section_id,
          isChange: false,
        }))
      );
    } else {
      setRows([]);
    }
  }, [getData?.data?.additionalFields]);

  // success toast message
  useEffect(() => {
    if (isAddSuccess) {
      toast("success", "Additional field added successfully");
    }
  }, [isAddSuccess]);
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Additional field update successfully");
    }
  }, [isSuccess]);
  useEffect(() => {
    if (isRemoveSuccess) {
      toast("success", "Additional field delete successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemoveSuccess]);

  return (
    <SidebarLayout title="Additional Custom Fields For Employee Profile">
      {open?.type === "REMOVE" ? (
        <ConfirmModal
          open={open?.open}
          handleClose={() => {
            handleClose();
          }}
          handleOk={handleRemove}
          confirmTitle="Delete"
          okText="Confirm"
          okLoading={isRemoveLoading}
        />
      ) : null}
      {loading || additionalDataLoading ? (
        <Loader />
      ) : (
        <div className="custom-form p-6 rounded-md bg-white border border-extra-light">
          <CustomTable
            loading={false}
            columns={rows?.length === 0 ? emptyColumns : columns}
            dataSource={rows?.length === 0 ? [{}] : addKeyInArray(rows)}
            className="header-table-color"
          />
          {/* <Space className="w-full mt-6" size={12}>
            <Button className="normal-dark-button modal-button" size="middle">
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
            >
              Save
            </Button>
          </Space> */}
        </div>
      )}
    </SidebarLayout>
  );
}

const typeList = [
  { label: "String", value: "string" },
  { label: "File / Image", value: "file" },
];
